import { PropsWithChildren, useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { clearQueryParamSilently, makeLogger } from '@cast/utils';

import { is403Error } from '@cast/api-client';
import {
  useUserAbilityOverrides,
  useAccessControlData,
} from '@cast/infrastructure-abilities';
import { useUser } from '@cast/infrastructure-auth';
import { useIsDemoModeDetermined } from '@cast/infrastructure-demo';
import { SuspenseLoader } from 'components/loaders';
import { ANALYTICS_ENABLED } from '@cast/constants';
import { promos } from 'constants/maps';
import { useAbility } from '@cast/shared-ability-data-access';
import { NO_ORGANIZATION_ACCESS_MESSAGE } from '@cast/shared-access-control-data-access';
import { AnalyticsInitializer } from '@cast/infrastructure-analytics';
import { activateDemoMiddleware } from 'core/api-middleware/demo-middleware/middlewares';
import { activateMockMiddleware } from 'core/api-middleware/mock-middleware/middlewares';
import { useConsumeCastAiWebCookie } from '../_hooks/useConsumeCastAiWebCookie';
import { useMarkReportingEligibleFromCastAiWebCookie } from '../_hooks/useMarkReportingEligibleFromCastAiWebCookie';
import {
  useAssetListClustersQuery,
  useClustersQuery,
} from '@cast/shared-cluster-context-data-access';
import { useInitSupportChat } from 'hooks/useInitSupportChat';
import {
  useCurrentOrganization,
  useNavigate,
} from '@cast/infrastructure-organization';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { PARAMS_TO_REMOVE } from '../../../constants';

const log = makeLogger('Initializer');

// Activate middleware at module level
activateDemoMiddleware();
activateMockMiddleware();

export const Initializer = ({ children }: PropsWithChildren<unknown>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const setUserAbilityOverridesUserId = useUserAbilityOverrides(
    (state) => state.setUserId
  );

  // Skip initialization loading on forbidden page to prevent infinite spinner
  const isForbiddenPage = location.pathname === '/forbidden';

  // Initialize user ability overrides when user ID becomes available
  useEffect(() => {
    setUserAbilityOverridesUserId(user.id);
  }, [user.id, setUserAbilityOverridesUserId]);

  const { isReady: isOrganizationReady, currentOrganization } =
    useOrganizations();
  const currentOrganizationForAnalytics = useCurrentOrganization();

  const analyticsDataReady = !!currentOrganization;
  const postHogOrganization = currentOrganizationForAnalytics
    ? {
        id: currentOrganizationForAnalytics.id,
        name: currentOrganizationForAnalytics.name,
      }
    : undefined;
  const { isLoading: rbacDataLoading } = useAccessControlData({});
  const abilitiesLoaded = !useAbility().isLoading;
  const clustersLoadingEnabled = isOrganizationReady && !rbacDataLoading;
  // Trigger queries for demo determination logic
  const { isLoading: clustersLoading, error: clustersError } = useClustersQuery(
    {
      enabled: clustersLoadingEnabled,
      withDiscovered: true,
    }
  );

  useEffect(() => {
    if (is403Error(clustersError) && !isForbiddenPage) {
      navigate('/forbidden', {
        state: {
          message: NO_ORGANIZATION_ACCESS_MESSAGE,
        },
      });
    }
  }, [clustersError, isForbiddenPage, navigate]);

  const analyticsInitializer = (
    <AnalyticsInitializer
      appName="console"
      enabled={analyticsDataReady}
      email={user.email}
      orgID={currentOrganization?.id ?? ''}
      orgName={currentOrganization?.name ?? ''}
      postHogOrganization={postHogOrganization}
    />
  );

  useAssetListClustersQuery({
    enabled: isOrganizationReady && abilitiesLoaded,
  });
  const clustersLoaded = clustersLoadingEnabled && !clustersLoading;

  // Demo mode is determined via middleware when getClusters/getAssetListClusters are called.
  const isDemoDeterminationProcessDone = useIsDemoModeDetermined();

  const criticalDataState = [
    isOrganizationReady,
    abilitiesLoaded,
    isDemoDeterminationProcessDone,
    clustersLoaded,
  ];

  useEffect(() => {
    // Remove Auth0 email verification params
    clearQueryParamSilently(PARAMS_TO_REMOVE);
  }, []);

  if (ANALYTICS_ENABLED) {
    criticalDataState.push(analyticsDataReady);
  }

  const applicationLoading = criticalDataState.some((isReady) => !isReady);

  log(
    `Critical data state`,
    JSON.stringify({
      isOrganizationReady,
      abilitiesLoaded,
      isDemoDeterminationProcessDone,
      clustersLoaded,
      applicationLoading,
    })
  );

  // Mark-eligible must finish (incl. trial-status refetch) before consume
  // clears the marketing cookie and before we render the main shell — otherwise
  // reporting signup briefly sees full Cast AI until refresh.
  const isReportingCookieEligibilityPending =
    useMarkReportingEligibleFromCastAiWebCookie(applicationLoading);
  useConsumeCastAiWebCookie(
    applicationLoading || isReportingCookieEligibilityPending
  );

  useInitSupportChat();

  // Skip loading check on forbidden page to allow error page to render
  if (
    (applicationLoading || isReportingCookieEligibilityPending) &&
    !isForbiddenPage
  ) {
    return (
      <>
        {analyticsInitializer}
        <SuspenseLoader />
      </>
    );
  }

  const promoQuery = new URLSearchParams(window.location.search).get('dest');
  if (promoQuery && Object.keys(promos).includes(promoQuery)) {
    navigate(promos[promoQuery], { replace: true });
  }

  return (
    <>
      {analyticsInitializer}
      {children}
    </>
  );
};
